import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Text} from '@priority-software/priority-style-react';
import cx from 'classnames';
import {GAP, MIN_VISIBLE_TAGS} from './constants';
import {EmailTag} from './EmailTag';
import {ruleDialogID} from '../../../../../../../constants';
import styles from './styles.module.scss';
import {ModernTooltip} from '../../../../../../../../../components/Common/Tooltip/ModernTooltip';

type Props = {
    data: Array<string>;
    onRemove: (value: string) => void;
};

export const EmailValues = (props: Props) => {
    const {data, onRemove} = props;
    const tagListRef = useRef<HTMLDivElement>();
    const countRef = useRef<HTMLDivElement>();
    const [expanded, setExpanded] = useState(false);
    const [visibleTagsWidth, setVisibleTagsWidth] = useState(0);
    const [visibleTagsCount, setVisibleTagsCount] = useState(MIN_VISIBLE_TAGS);
    const [maxTagAvailbleSpace, setMaxTagAvailbleSpace] = useState(0);

    const updateTagsVisibility = useCallback(() => {
        const parentEl = tagListRef.current.parentElement;
        const tags = tagListRef.current.children;
        const countMaxWidth = countRef.current?.offsetWidth || 0;

        let visibleWidth = 0;
        let visibleTagsCount = 0;

        let listMaxWidth = 0;

        for (let i = 0; i < tags.length; i++) {
            listMaxWidth =
                i === tags.length - 1 ? parentEl.offsetWidth : parentEl.offsetWidth - countMaxWidth;

            const tagWidth = (tagListRef.current.children[i] as HTMLDivElement).offsetWidth;
            const upcomingWidth = visibleWidth + tagWidth + GAP;

            if (upcomingWidth <= listMaxWidth) {
                visibleWidth = upcomingWidth;
                visibleTagsCount++;
            } else {
                break;
            }
        }
        setMaxTagAvailbleSpace(listMaxWidth);
        setVisibleTagsCount(visibleTagsCount || MIN_VISIBLE_TAGS);
        setVisibleTagsWidth(visibleWidth);
    }, []);

    useLayoutEffect(() => {
        const parentEl = tagListRef.current.parentElement;

        const tagListMutationObserver = new MutationObserver(updateTagsVisibility);
        const tagListResizeObserver = new ResizeObserver(updateTagsVisibility);

        tagListMutationObserver.observe(tagListRef.current, {childList: true});
        tagListResizeObserver.observe(parentEl);

        // Cleanup the observer on component unmount
        return () => {
            tagListMutationObserver.disconnect();
            tagListResizeObserver.disconnect();
        };
    }, [updateTagsVisibility]);

    const onCountClick = useCallback(() => {
        setExpanded(true);
    }, []);

    const tooltip = useMemo(() => {
        return (
            <div>
                {data.slice(visibleTagsCount, data.length).map(item => {
                    return (
                        <div key={item} dir="ltr" className={styles.ellipsis}>
                            {item}
                        </div>
                    );
                })}
            </div>
        );
    }, [data, visibleTagsCount]);

    return (
        <div className={cx(styles.component, {[styles.expanded]: expanded})}>
            <div
                className={styles.tags}
                ref={tagListRef}
                style={{
                    gap: GAP,
                    maxWidth: expanded || !visibleTagsWidth ? '100%' : visibleTagsWidth
                }}
            >
                {data.map((value, index) => {
                    return (
                        <EmailTag
                            key={value}
                            maxWidth={maxTagAvailbleSpace}
                            isVisible={expanded || index < visibleTagsCount}
                            value={value}
                            onRemove={onRemove}
                        />
                    );
                })}
            </div>

            {!expanded && visibleTagsCount < data.length ? (
                <div className={styles.count} ref={countRef}>
                    <ModernTooltip tooltip={tooltip} portalId={ruleDialogID} position="top">
                        <Text
                            tag="span"
                            skin="paragraph-2"
                            textColor="primary-500"
                            onClick={onCountClick}
                        >
                            +{data.length - visibleTagsCount}
                        </Text>
                    </ModernTooltip>
                </div>
            ) : null}
        </div>
    );
};
